import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import Header from "../NewUi/Header/header";

import styles from "./Myplan.module.scss";

type Subscription = {
  id: number | string;
  subscription_name?: string;
  status?: string;
  renewal_type?: string;
  renewal_date?: string;
  expire_date?: string;
  max_no_of_sites?: number;
  flash_message?: string;
};

type Site = {
  id: number | string;
  name: string;
  active: boolean;
};

type SubscriptionSite = {
  site_id: number | string;
};

function formatDate(value?: string) {
  if (!value) return "Not available";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function formatLabel(value?: string) {
  if (!value) return "Not available";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function MyPlan() {
  const getMyPlan = useQuery(["myPlan"], async () => {
    const { data } = await axios.get<Subscription>("/subscription");
    return data;
  });

  const plan = getMyPlan.data;
  const normalizedStatus = plan?.status?.toLowerCase() ?? "";
  const isActive = normalizedStatus === "active";
  const isInactive = normalizedStatus === "inactive";
  const statusStyle = isInactive
    ? styles.statusInactive
    : isActive
    ? styles.statusActive
    : styles.statusNeutral;

  const getMyPlanLink = useQuery(
    ["myPlanLink"],
    async () => {
      try {
        const { data } = await axios.get<string>(
          "/user_purchase/get_stripe_customer_portal_link"
        );
        return data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
          return null;
        }
        throw error;
      }
    },
    {
      enabled: Boolean(plan?.id),
    }
  );

  const getMySites = useQuery(
    ["mySites", plan?.id],
    async () => {
      const { data } = await axios.get<SubscriptionSite[]>(
        `/subscription/get_existing_sites_under_subscription?subscription_id=${plan?.id}`
      );
      return data;
    },
    {
      enabled: Boolean(plan?.id),
    }
  );

  const getAllSites = useQuery(
    ["allSites"],
    async () => {
      const { data } = await axios.get<Site[]>(
        "/subscription/get_site_list_for_subscription"
      );
      return data;
    },
    {
      enabled: Boolean(plan?.id),
    }
  );

  const addSite = useMutation(
    async (siteId: number | string) => {
      const { data } = await axios.put(
        `/subscription/add_site_to_subscription?subscription_id=${plan?.id}&site_id=${siteId}`
      );
      return data;
    },
    {
      onSuccess: () => {
        getMySites.refetch();
        toast.success("Site access enabled successfully.");
      },
      onError: () => {
        toast.error("Unable to enable site access. Please try again.");
      },
    }
  );

  const deleteSite = useMutation(
    async (siteId: number | string) => {
      const { data } = await axios.delete(
        `/subscription/remove_site_from_subscription?subscription_id=${plan?.id}&site_id=${siteId}`
      );
      return data;
    },
    {
      onSuccess: () => {
        getMySites.refetch();
        toast.success("Site access removed successfully.");
      },
      onError: () => {
        toast.error("Unable to remove site access. Please try again.");
      },
    }
  );

  const clearMessage = useMutation(
    async () => {
      return axios.put(
        `/subscription/clear_flash_message?subscription_id=${plan?.id}`
      );
    },
    {
      onSuccess: () => {
        getMyPlan.refetch();
      },
      onError: () => {
        toast.error("Unable to clear the message.");
      },
    }
  );

  const selectedSites = getMySites.data ?? [];
  const activeSites = (getAllSites.data ?? []).filter((site) => site.active);
  const maxSites = plan?.max_no_of_sites ?? 0;
  const isUpdatingSite = addSite.isLoading || deleteSite.isLoading;

  const hasSiteAccess = (siteId: number | string) =>
    selectedSites.some((site) => String(site.site_id) === String(siteId));

  const handleSiteAccessChange = (
    siteId: number | string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      if (selectedSites.length >= maxSites) {
        toast.error(
          `Your plan allows access to a maximum of ${maxSites} ${
            maxSites === 1 ? "site" : "sites"
          }.`
        );
        return;
      }

      addSite.mutate(siteId);
      return;
    }

    deleteSite.mutate(siteId);
  };

  if (getMyPlan.isLoading) {
    return (
      <>
        <Header />
        <main className={styles.myPlan}>
          <div className={styles.loadingState}>
            <CircularProgress size={28} />
            <p>Loading your subscription...</p>
          </div>
        </main>
      </>
    );
  }

  if (getMyPlan.isError || !plan) {
    return (
      <>
        <Header />
        <main className={styles.myPlan}>
          <Alert severity="error">
            We could not load your subscription. Please refresh the page and
            try again.
          </Alert>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className={styles.myPlan}>
      {plan.flash_message && (
        <Alert
          severity="info"
          className={styles.notice}
          onClose={() => clearMessage.mutate()}
        >
          {plan.flash_message}
        </Alert>
      )}

      {isInactive && (
        <div className={styles.inactiveBanner} role="status">
          <div>
            <strong>Your subscription is inactive</strong>
            <p>
              Reactivate your account to restore access to your plan features.
            </p>
          </div>
          <a href="/plans">View available plans</a>
        </div>
      )}

      <section className={styles.heroCard}>
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}>Subscription</span>
          <div className={styles.titleRow}>
            <h1>My Plan</h1>
            <span
              className={`${styles.statusBadge} ${statusStyle}`}
            >
              {isInactive ? "Ended" : formatLabel(plan.status)}
            </span>
          </div>
          <p>
            Review your subscription, billing details, and site access in one
            place.
          </p>
        </div>

        <Button
          href={getMyPlanLink.data || undefined}
          target="_blank"
          rel="noreferrer"
          variant="contained"
          className={styles.billingButton}
          disabled={!getMyPlanLink.data || getMyPlanLink.isLoading}
          startIcon={<PaidIcon />}
        >
          Manage billing
        </Button>
      </section>

      <section className={styles.summaryCard}>
        <div className={styles.sectionHeading}>
          <div>
            <h2>Plan overview</h2>
            <p>Your current subscription details.</p>
          </div>
        </div>

        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span>Active plan</span>
            <strong>{plan.subscription_name || "No plan selected"}</strong>
          </div>
          <div className={styles.summaryItem}>
            <span>Status</span>
            <strong>{isInactive ? "Ended" : formatLabel(plan.status)}</strong>
          </div>
          <div className={styles.summaryItem}>
            <span>Renewal type</span>
            <strong>{formatLabel(plan.renewal_type)}</strong>
          </div>
          <div className={styles.summaryItem}>
            <span>{isInactive ? "Ended on" : "Next renewal"}</span>
            <strong>
              {formatDate(isInactive ? plan.expire_date : plan.renewal_date)}
            </strong>
          </div>
        </div>
      </section>

      {plan.subscription_name !== "Associate Dentist" && (
        <section className={styles.sectionCard}>
          <div className={styles.sectionHeading}>
            <div>
              <h2>Site access</h2>
              <p>
                Choose which sites are included in this subscription. Changes
                are saved automatically.
              </p>
            </div>
            <span className={styles.usageBadge}>
              {selectedSites.length} of {maxSites} selected
            </span>
          </div>

          {getAllSites.isLoading || getMySites.isLoading ? (
            <div className={styles.inlineLoading}>
              <CircularProgress size={24} />
              <span>Loading sites...</span>
            </div>
          ) : getAllSites.isError || getMySites.isError ? (
            <Alert severity="error">
              We could not load your sites. Please refresh the page and try
              again.
            </Alert>
          ) : activeSites.length === 0 ? (
            <div className={styles.emptyState}>
              <strong>No active sites found</strong>
              <p>Active sites will appear here when they become available.</p>
            </div>
          ) : (
            <FormGroup className={styles.siteForm}>
              <div className={styles.tableShell}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Site</th>
                      <th scope="col">Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSites.map((site, index) => {
                      const isSelected = hasSiteAccess(site.id);

                      return (
                        <tr key={site.id}>
                          <td>{index + 1}</td>
                          <td>
                            <span className={styles.siteName}>{site.name}</span>
                          </td>
                          <td>
                            <FormControlLabel
                              className={styles.accessControl}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  disabled={isUpdatingSite || isInactive}
                                  onChange={(event) =>
                                    handleSiteAccessChange(site.id, event)
                                  }
                                  inputProps={{
                                    "aria-label": `${
                                      isSelected ? "Remove" : "Enable"
                                    } access for ${site.name}`,
                                  }}
                                />
                              }
                              label={isSelected ? "Enabled" : "Not enabled"}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </FormGroup>
          )}
        </section>
      )}
      </main>
    </>
  );
}

export default MyPlan;
